import Diem from '@travelnest/diem';
import { IEvent } from '@travelnest/typewriter';
import { EventStatus } from '@travelnest/typewriter';

export type DateRange = [Date, Date];

export const datesToRanges = (dates: Date[]): DateRange[] => {
	const ranges: DateRange[] = [];

	for (let i = 0; i < dates.length; i++) {
		if (i === dates.length - 1) {
			// if we hit the end of the list, we are done
			ranges.push([dates[i], dates[i]]);
			return ranges;
		}
		// find the next non-contiguous date in the list
		for (let j = i + 1; j < dates.length; j++) {
			const end = j === dates.length - 1;
			const gap = Math.abs(dateDiff(dates[j], dates[j - 1])) > 1;
			if (gap) {
				// if we find a date gap, push the contiguous range
				ranges.push([dates[i], dates[j - 1]]);
				// then start the next loop from the gap we found
				i = j - 1;
				break;
			}
			if (end) {
				// if we hit the end of the loop with contiguous dates, we are done
				ranges.push([dates[i], dates[j]]);
				return ranges;
			}
		}
	}

	return ranges;
};

export const rangesToDates = (ranges: DateRange[]): Date[] => {
	return ranges.reduce((dates, range) => {
		const filler = new Array(dateDiff(...range) + 1).fill(null).map((_, i) => {
			const d = new Date(range[0]);
			d.setDate(d.getDate() + i);
			return d;
		});
		return [...dates, ...filler];
	}, []);
};

export const dateDiff = (start: Date, end: Date): number => {
	const diff = (end.getTime() - start.getTime()) / 60 / 1000;
	return Math.round(diff / 60 / 24);
};

export const ordinal = (num: number) => {
	const order = num % 10;
	const teens = num % 100;
	if (order === 1 && teens !== 11) return num + 'st';
	if (order === 2 && teens !== 12) return num + 'nd';
	if (order === 3 && teens !== 13) return num + 'rd';
	return num + 'th';
};

export const statusToTextMapper = (status: EventStatus | 'synced', event: IEvent) => {
	switch (status) {
		case 'booked':
			return event.booking?.guest?.name || 'booking';
		case 'synced':
			return 'synced';
		default:
			return status;
	}
};

/**
 * @param date A date string in format YYYY-MM-DD
 * @returns A date object representing the same date in local time
 */
export const dateStringToDate = (date: string): Date => {
	// adjusts date for user's current timezone
	const d = new Date(date);
	return new Date(d.setMinutes(d.getMinutes() + d.getTimezoneOffset()));
};

export const stripTimezone = (d: Diem) => {
	return dateStringToDate(d.toISOString());
};

export const statusMapper = (event: IEvent): EventStatus | 'synced' => event.source.includes('import:') ? 'synced' : event.status;

export const incrementDate = (date: Date, increment: number) => {
	const d = new Date(date);
	d.setDate(d.getDate() + increment);
	return d;
};

/**
 * Takes an import source and normalises it for consumption by the calendar
 * Will remove all source parts after the first two, e.g. import:booking.com:12345
 * will be returned as import:booking.com
 * @param source The source of the synced event e.g. import:airbnb.com
 */
export const normaliseImportedEventSource = (source: string) => {
	const parts = (source || '').split(':');
	const [type = '', channel = ''] = parts;
	if (type && !channel) return type;
	if (!type && channel) return channel;
	if (!type && !channel) return '';
	return `${type}:${channel}`;
};

/**
 * Retrieve an event status, text, and additional data to help display events in the calendar.
 * @param event {IEvent} - The event to generate the data for.
 */
export const buildEventData = (event: IEvent): [string, string | undefined, any] => {
	const status = statusMapper(event);
	const text = statusToTextMapper(status, event);
	let data;
	if (status === 'booked') {
		data = {
			name: event.booking?.guest.name,
			guests: (event.booking?.guest.adults || 0) + (event.booking?.guest.infants || 0) + (event.booking?.guest.children || 0),
			email: event.booking?.guest.email,
			propertyId: event.property_id,
			channel: event.source,
			managedBy: event.managed_by,
		};
	}

	if (status === 'synced') {
		data = {
			channel: normaliseImportedEventSource(event.source),
			managedBy: event.managed_by,
		};
	}
	return [status, text, data];
};

export const dateEquals = (d1: Date, d2: Date) => {
	return dateDiff(d1, d2) === 0;
};

export const stripDuplicateDates = (dates: Date[]) => dates.reduce((newDates, currentDate) => {
	if (newDates.find((date) => dateEquals(date, currentDate))) {
		return newDates;
	}
	return [...newDates, currentDate];
}, []);

export const getIndividualDatesInRange = (range: [Date, Date]): Date[] => {
	const dates = [];
	const start = range[0];
	const end = range[1];
	dates.push(start);
	// if start and end are the same, it's only one day
	if (start !== end) {
		const differenceInTime = end.getTime() - start.getTime();
		const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));
		for (let i = 0; i < differenceInDays; i++) {
			dates.push(incrementDate(start, i + 1));
		}
	}
	return dates;
};

export const getISODateStringNoTime = (date: Date): string => {
	const year = date.getFullYear();
	const month = date.getMonth() + 1; // months are 0 indexed so Jan = 0
	const day = date.getDate();

	const monthTwoChar = month < 10 ? `0${month}` : month;
	const dayTwoChar = day < 10 ? `0${day}` : day;

	const dateString = `${year}-${monthTwoChar}-${dayTwoChar}`;

	return dateString;
};
